/**
 * Copyright (c) 2017-present, Tomaruba, Inc.
 * All rights reserved.
 *
 */
import { ReduceStore } from 'flux/utils'
import AppDispatcher from '../../core/AppDispatcher'
import { Record, List } from 'immutable'
import { compose, map } from 'ramda'
import { dateToInput } from '../../util/DateString'
import { CheckInState } from '../state/CheckInState'
import CheckInEvent from '../../event/CheckInEvent'

const CheckInFormState = Record({
  completed: false,
  forms: List.of(new CheckInState()),
  expired: false,
  deletes: List([])
})

class CheckInFormStore extends ReduceStore {
  constructor() {
    super(AppDispatcher)
  }

  getInitialState() {
    return new CheckInFormState()
  }

  reduce(state, action) {
    const payload = action.payload

    switch (action.type) {
      case CheckInEvent.initialize:
        return new CheckInFormState()

      case CheckInEvent.fetchOne:
        const reservation = payload.reservation
        const phone = reservation.phone_number || ''
        const mobile = reservation.mobile_number || ''
        const arrival = dateToInput(reservation.check_in)
        const departure = dateToInput(reservation.check_out)
        let nation = ''
        if (!reservation.channel_manager || reservation.channel_manager === 'neppan') {
          nation = 'Japan'
        }

        if (payload.check_ins.length > 0) {
          return state.set(
            'forms',
            List(
              payload.check_ins.map(c => {
                // パスポート画像のURLあったらチェックいれた状態にする
                const with_passport = c.passport_url ? true : false
                return new CheckInState(c).merge({
                  with_passport: with_passport
                })
              })
            )
          )
        } else {
          return state.set(
            'forms',
            state.forms.map(x =>
              x.merge({
                phone: phone,
                mobile: mobile,
                arrival: arrival,
                departure: departure,
                nation: nation
              })
            )
          )
        }

      case CheckInEvent.addGuest:
        const arrival_copy = state.forms.get(0).arrival || ''
        const departure_copy = state.forms.get(0).departure || ''
        const address_copy = state.forms.get(0).address || ''
        const phone_copy = state.forms.get(0).phone || ''
        const email_copy = state.forms.get(0).email || ''
        const nation_copy = state.forms.get(0).nation || ''
        return state.set(
          'forms',
          state.forms.push(
            new CheckInState({
              address: address_copy,
              phone: phone_copy,
              email: email_copy,
              nation: nation_copy,
              arrival: arrival_copy,
              departure: departure_copy
            })
          )
        )

      case CheckInEvent.confirmDeletion:
        return state.set('forms', this.updateShowConfirmDeletion(payload.index, true)(state.forms))

      case CheckInEvent.closeDeletionDialog:
        return state.set('forms', this.updateShowConfirmDeletion(payload.index, false)(state.forms))

      case CheckInEvent.deleteForm:
        const checkIn = state.forms.getIn([payload.index - 1])
        let _state = state
        if (checkIn.id) {
          _state = _state.set('deletes', _state.deletes.push(checkIn.id))
        }
        return _state.set(
          'forms',
          _state.forms.filter((x, idx) => idx + 1 !== payload.index)
        )

      case CheckInEvent.onEdit:
        return state.set('forms', state.forms.setIn([payload.index - 1, payload.name], payload.value))

      case CheckInEvent.disposePassport:
        return state.set(
          'forms',
          state.forms.map((x, idx) => {
            if (idx + 1 === payload.index) {
              return x.merge({ passport_url: null, passport_image: null })
            } else {
              return x
            }
          })
        )

      case CheckInEvent.complete:
        return state.set('completed', true)

      case CheckInEvent.expired:
        return state.set('expired', true)

      default:
        return state
    }
  }

  updateShowConfirmDeletion = (index, flag) =>
    compose(
      map((x, idx) => {
        return index === idx + 1 ? x.merge({ showConfirmDeletion: flag }) : x
      })
    )
}

export default new CheckInFormStore()
